<template>
	<transition :key="langKey" name="viewer-fade">
		<div v-if="true" ref="viewer" tabindex="-1" class="to-pic-viewer" :style="{ 'z-index': viewerZIndex }">
			<!-- 遮罩 -->
			<div class="to-pic-viewer-mask" @click.self="handleMaskClick"></div>

			<!-- CLOSE -->
			<to-icon class="to-pic-viewer-close" value="close" link @click="hide"></to-icon>

			<!-- ARROW -->
			<template v-if="!isSingle">
				<to-icon v-dis="!infinite && isFirst" class="to-pic-viewer-prev" value="back" link @click="prev"></to-icon>
				<to-icon v-dis="!infinite && isLast" class="to-pic-viewer-next" value="next" link @click="next"></to-icon>
			</template>

			<!-- ACTIONS -->
			<div class="to-pic-viewer-fn">
				<to-button class="to-pic-viewer-zoomout" icon="zoomout" @click="handleActions('zoomOut')"></to-button>
				<to-button class="to-pic-viewer-zoomin" icon="zoomin" @click="handleActions('zoomIn')"></to-button>
				<to-button class="to-pic-viewer-full" @click="toggleMode()">{{ mode.name === 'contain' ? t('to.pic.viewer.full') : t('to.pic.viewer.restore') }}</to-button>
				<to-button class="to-pic-viewer-counterclockwise" icon="leftrotation" @click="handleActions('anticlocelise')"></to-button>
				<to-button class="to-pic-viewer-clockwise" icon="reset" @click="handleActions('clocelise')"></to-button>
			</div>

			<!-- CANVAS -->
			<div class="to-pic-viewer-list">
				<img v-for="url in urlList.filter((item, i) => i === index)" ref="img" :key="url" :src="currentImg" :style="imgStyle" @load="handleImgLoad" @error="handleImgError" @mousedown="handleMouseDown" @touchstart="handleMouseDown" />
			</div>
		</div>
	</transition>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { ToIcon, ToButton } from '@touchvue/components'
import { vDis } from '@touchvue/directives'
import { t, langKey } from '@touchvue/locale'

// 临时实现工具函数，实际项目中应该从对应的工具库导入
const on = (element: Document | Element, event: string, handler: EventListener) => {
	element.addEventListener(event, handler)
}

const off = (element: Document | Element, event: string, handler: EventListener) => {
	element.removeEventListener(event, handler)
}

const rafThrottle = (fn: (...args: any[]) => void) => {
	let ticking = false
	return function (this: any, ...args: any[]) {
		if (!ticking) {
			requestAnimationFrame(() => {
				fn.apply(this, args)
				ticking = false
			})
			ticking = true
		}
	}
}

const isFirefox = () => {
	return navigator.userAgent.toLowerCase().indexOf('firefox') > -1
}

export interface ModeType {
	name: string
}

const ModeEnum = {
	CONTAIN: {
		name: 'contain'
	},
	ORIGINAL: {
		name: 'original'
	}
}

const mousewheelEventName = isFirefox() ? 'DOMMouseScroll' : 'mousewheel'

export interface Props {
	urlList: string[]
	zIndex: number
	onSwitch?: (index: number) => void
	onClose?: () => void
	initialIndex: number
	maskClosable: boolean
}

const props = withDefaults(defineProps<Props>(), {
	urlList: () => [],
	zIndex: 2000,
	onSwitch: () => {},
	onClose: () => {},
	initialIndex: 0,
	maskClosable: true
})

const viewer = ref<HTMLElement>()
const img = ref<HTMLImageElement[]>()
const index = ref(props.initialIndex)
const infinite = ref(true)
const loading = ref(false)
const mode = ref<ModeType>(ModeEnum.CONTAIN)
const transform = ref({
	scale: 1,
	deg: 0,
	offsetX: 0,
	offsetY: 0,
	enableTransition: false
})

let _keyDownHandler: ((e: KeyboardEvent) => void) | null = null
let _mouseWheelHandler: ((e: WheelEvent) => void) | null = null
let _dragHandler: ((e: MouseEvent | TouchEvent) => void) | null = null

const isSingle = computed(() => props.urlList.length <= 1)
const isFirst = computed(() => index.value === 0)
const isLast = computed(() => index.value === props.urlList.length - 1)
const currentImg = computed(() => props.urlList[index.value])

const imgStyle = computed(() => {
	const { scale, deg, offsetX, offsetY, enableTransition } = transform.value
	const style: Record<string, string> = {
		transform: `scale(${scale}) rotate(${deg}deg)`,
		transition: enableTransition ? 'transform .3s' : '',
		'margin-left': `${offsetX}px`,
		'margin-top': `${offsetY}px`
	}
	if (mode.value === ModeEnum.CONTAIN) {
		style.maxWidth = style.maxHeight = '100%'
	}
	return style
})

const viewerZIndex = computed(() => {
	const nextZIndex = (window as any).$TouchUI?.zIndex || 2000
	if ((window as any).$TouchUI) {
		;(window as any).$TouchUI.zIndex++
	}
	return props.zIndex > nextZIndex ? props.zIndex : nextZIndex
})

watch(index, val => {
	reset()
	props.onSwitch?.(val)
})

watch(currentImg, async () => {
	await nextTick()
	const $img = img.value?.[0]
	if ($img && !$img.complete) {
		loading.value = true
	}
})

const hide = () => {
	deviceSupportUninstall()
	props.onClose?.()
}

const deviceSupportInstall = () => {
	_keyDownHandler = (e: KeyboardEvent) => {
		e.stopPropagation()
		const keyCode = e.keyCode
		switch (keyCode) {
			case 27: // ESC
				hide()
				break
			case 32: // SPACE
				toggleMode()
				break
			case 37: // LEFT_ARROW
				prev()
				break
			case 38: // UP_ARROW
				handleActions('zoomIn')
				break
			case 39: // RIGHT_ARROW
				next()
				break
			case 40: // DOWN_ARROW
				handleActions('zoomOut')
				break
		}
	}

	_mouseWheelHandler = rafThrottle((e: WheelEvent) => {
		e.preventDefault()
		const delta = e.deltaY ? -e.deltaY : (e as any).wheelDelta ? (e as any).wheelDelta : -(e as any).detail
		if (delta > 0) {
			handleActions('zoomIn', {
				zoomRate: 0.015,
				enableTransition: false
			})
		} else {
			handleActions('zoomOut', {
				zoomRate: 0.015,
				enableTransition: false
			})
		}
	})

	on(document, 'keydown', _keyDownHandler)
	on(document, mousewheelEventName, _mouseWheelHandler)
}

const deviceSupportUninstall = () => {
	if (_keyDownHandler) {
		off(document, 'keydown', _keyDownHandler)
		_keyDownHandler = null
	}
	if (_mouseWheelHandler) {
		off(document, mousewheelEventName, _mouseWheelHandler)
		_mouseWheelHandler = null
	}
}

const handleImgLoad = () => {
	loading.value = false
}

const handleImgError = (e: Event) => {
	loading.value = false
	;(e.target as HTMLImageElement).alt = t('pic.tip.error.load')
}

const handleMouseDown = (e: MouseEvent | TouchEvent) => {
	if (loading.value || (!(e as TouchEvent).touches && (e as MouseEvent).button !== 0)) return

	const { offsetX, offsetY } = transform.value
	let startX = (e as MouseEvent).pageX
	let startY = (e as MouseEvent).pageY

	if ((e as TouchEvent).touches) {
		startX = (e as TouchEvent).touches[0].pageX
		startY = (e as TouchEvent).touches[0].pageY
	}

	_dragHandler = rafThrottle((ev: MouseEvent | TouchEvent) => {
		transform.value.offsetX = offsetX + (ev as MouseEvent).pageX - startX
		transform.value.offsetY = offsetY + (ev as MouseEvent).pageY - startY
		if ((ev as TouchEvent).touches) {
			transform.value.offsetX = offsetX + (ev as TouchEvent).touches[0].pageX - startX
			transform.value.offsetY = offsetY + (ev as TouchEvent).touches[0].pageY - startY
		}
	})

	const handleMouseUp = () => {
		if (_dragHandler) {
			off(document, 'mousemove', _dragHandler)
			off(document, 'touchmove', _dragHandler)
		}
	}

	on(document, 'mousemove', _dragHandler)
	on(document, 'mouseup', handleMouseUp)
	on(document, 'touchmove', _dragHandler)
	on(document, 'touchend', handleMouseUp)

	e.preventDefault()
}

const handleMaskClick = () => {
	if (props.maskClosable) {
		hide()
	}
}

const reset = () => {
	transform.value = {
		scale: 1,
		deg: 0,
		offsetX: 0,
		offsetY: 0,
		enableTransition: false
	}
}

const toggleMode = () => {
	if (loading.value) return
	const modeNames = Object.keys(ModeEnum) as (keyof typeof ModeEnum)[]
	const modeValues = Object.values(ModeEnum)
	const currentIndex = modeValues.indexOf(mode.value)
	const nextIndex = (currentIndex + 1) % modeNames.length
	mode.value = ModeEnum[modeNames[nextIndex]]
	reset()
}

const prev = () => {
	if (isFirst.value && !infinite.value) return
	const len = props.urlList.length
	index.value = (index.value - 1 + len) % len
}

const next = () => {
	if (isLast.value && !infinite.value) return
	const len = props.urlList.length
	index.value = (index.value + 1) % len
}

const handleActions = (action: string, options: any = {}) => {
	if (loading.value) return
	const { zoomRate = 0.2, rotateDeg = 90, enableTransition = true } = options

	switch (action) {
		case 'zoomOut':
			if (transform.value.scale > 0.2) {
				transform.value.scale = parseFloat((transform.value.scale - zoomRate).toFixed(3))
			}
			break
		case 'zoomIn':
			transform.value.scale = parseFloat((transform.value.scale + zoomRate).toFixed(3))
			break
		case 'clocelise':
			transform.value.deg += rotateDeg
			break
		case 'anticlocelise':
			transform.value.deg -= rotateDeg
			break
	}
	transform.value.enableTransition = enableTransition
}

onMounted(() => {
	deviceSupportInstall()
	viewer.value?.focus()
})
</script>
